import React from "react";
import PropTypes from 'prop-types';

import Tooltip from '@components/common/tooltip/main';
import {
  Collection,
  Brandfolder,
  bulkRefreshSections,
  pluralizeObject
} from "@helpers/show_page_helpers";

export default class BulkAction extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      approved: this.props.assetsApproved,
      disableAction: false
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.assetsApproved !== prevProps.assetsApproved) {
      this.initialApprovalStatus();
    }
  }

  initialApprovalStatus() {
    this.setState({ approved: this.props.assetsApproved });
  }

  buildDataBody() {
    const { actionType, selectedAssetKeys } = this.props;

    if (actionType === 'BulkApproval') {
      return { data: { asset_keys: Array.from(selectedAssetKeys) } };
    }

    if (actionType === 'BulkCollectionRemoval') {
      return { data: { asset_keys: Array.from(selectedAssetKeys), collection_keys: [Collection.key] } };
    }

    return null;
  }

  removeAssetsFromCollection() {
    this.setState({ disableAction: true });

    const assetQtyObject = pluralizeObject(this.props.selectedAssetKeys);
    const assetCopy = assetQtyObject.plural ? t('assets') : t('asset');
    const remainCopy = assetQtyObject.plural ? t('remain') : t('remains');

    $.ajax({
      type: "DELETE",
      url: '/api/v4/bulk_actions/assets/destroy_collection_associations?queue_priority=high',
      contentType: "application/json",
      data: JSON.stringify(this.buildDataBody()),
      context: this,
      headers: { Authorization: `Bearer ${BF_Token}` },
      success: () => {
        this.setState({ disableAction: false });
        BF.fx.updateAssetPageCount(assetQtyObject.qty, false);
        this.props.addRemoveSelected([]);
        bulkRefreshSections(this.props.selectedAssetKeys);
        Notify.create({
          title: `${t('Removed')} ${assetQtyObject.qty} ${assetCopy} ${t('from')} ${Collection.name}.`,
          body: `${t('Removed')} ${assetCopy} ${t('still')} ${remainCopy} ${t('in')} ${Brandfolder.name}.`,
          type: 'success'
        });
      },
      error: () => {
        this.setState({ disableAction: false });
        Notify.create({
          title: `${t('An error occurred when removing')} ${assetQtyObject.qty} ${assetCopy} ${t('from')} ${Collection.name}. ${t('Please try again.')}`,
          type: 'error'
        });
      }
    });
  }

  approveAssets() {
    this.setState({ disableAction: true });

    $.ajax({
      type: "POST",
      url: '/api/v4/bulk_actions/assets/approve?queue_priority=high',
      contentType: "application/json",
      data: JSON.stringify(this.buildDataBody()),
      context: this,
      headers: { Authorization: `Bearer ${BF_Token}` },
      success: (response) => {
        this.setState((prevState) => ({ approved: !prevState.approved, disableAction: false }));
        bulkRefreshSections(this.props.selectedAssetKeys);
        const approvalQty = response.required_approvals;
        const approvalCopy = approvalQty === 1 ? t('total approval') : t('total approvals');
        const bodyText = (approvalQty === 'N/A' || !approvalQty)
          ? ''
          : `${t('This asset requires')} ${approvalQty} ${approvalCopy}.`;
        this.makeNotification(t('Approved'), bodyText);
      },
      error: () => {
        this.setState({ disableAction: false });
        Notify.create({
          title: t('An error occurred when approving assets. Please try again.'),
          type: 'error'
        });
      }
    });
  }

  unapproveAssets() {
    this.setState({ disableAction: true });
    const { activeCdnTracers } = this.props;
    const that = this;

    if (activeCdnTracers && activeCdnTracers > 0) {
      const sweet_alert_options = {
        title: t("An asset has active external links."),
        text: t("At least one of your selected assets has external links. \n Unapproving will cause broken image links on sites that depend on the availability of these assets. Check the usage tab to see active external links before unapproving.\n\n"),
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        confirmButtonText: "Unapprove",
        closeOnConfirm: true,
        customClass: "merging"
      };
      swal(sweet_alert_options, (unapprove_selected) => { // eslint-disable-line no-undef
        if (unapprove_selected) {
          $.ajax({
            type: "POST",
            url: '/api/v4/bulk_actions/assets/unapprove?queue_priority=high',
            contentType: "application/json",
            data: JSON.stringify(that.buildDataBody()),
            context: this,
            headers: { Authorization: `Bearer ${BF_Token}` },
            success: (response) => {
              that.setState((prevState) => ({ approved: !prevState.approved, disableAction: false }));
              bulkRefreshSections(that.props.selectedAssetKeys);
              const approvalQty = response.required_approvals;
              const approvalCopy = approvalQty === 1 ? t('approval') : t('approvals');
              const bodyText = (approvalQty === 'N/A' || !approvalQty)
                ? ''
                : `${t('This asset requires')} ${approvalQty} ${approvalCopy}. ${t('All previous approvals are void.')}`;
              that.makeNotification(t('Unapproved'), bodyText);
            },
            error: () => {
              that.setState({ disableAction: false });
              Notify.create({
                title: t('An error occurred when unapproving assets. Please try again.'),
                type: 'error'
              });
            }
          });
        }
      });
    } else {
      $.ajax({
        type: "POST",
        url: '/api/v4/bulk_actions/assets/unapprove?queue_priority=high',
        contentType: "application/json",
        data: JSON.stringify(this.buildDataBody()),
        context: this,
        headers: { Authorization: `Bearer ${BF_Token}` },
        success: (response) => {
          this.setState((prevState) => ({ approved: !prevState.approved, disableAction: false }));
          bulkRefreshSections(this.props.selectedAssetKeys);
          const approvalQty = response.required_approvals;
          const approvalCopy = approvalQty === 1 ? t('approval') : t('approvals');
          const bodyText = (approvalQty === 'N/A' || !approvalQty)
            ? ''
            : `${t('This asset requires')} ${approvalQty} ${approvalCopy}. ${t('All previous approvals are void.')}`;
          this.makeNotification(t('Unapproved'), bodyText);
        },
        error: () => {
          this.setState({ disableAction: false });
          Notify.create({
            title: t('An error occurred when unapproving assets. Please try again.'),
            type: 'error'
          });
        }
      });
    }
  }

  makeNotification(approvalVerb, bodyText) {
    const assetQtyObject = pluralizeObject(this.props.selectedAssetKeys);
    const assetCopy = assetQtyObject.plural ? t('assets') : t('asset');

    Notify.create({
      title: `${approvalVerb} ${assetQtyObject.qty} ${assetCopy}!`,
      body: bodyText,
      type: 'success'
    });
  }

  approvalStatus() {
    if (this.state.approved) {
      return { icon: 'bff-unapprove', copy: t('Unapprove'), action: () => this.unapproveAssets() };
    }

    return { icon: 'bff-approve', copy: t('Approve'), action: () => this.approveAssets() };
  }

  renderBulkApproval(disabledClass) {
    if (!this.props.approver) { return null; }

    return (
      <Tooltip
        tooltipId="BulkApproval-tooltip"
        className="Approve-tooltip-component"
        tooltipContent={this.approvalStatus().copy}
        type="light"
        offset={`{'top': -20}`}
      >
        <div
          className={`bulk-bar-item hover-item ${disabledClass}`}
          onClick={this.approvalStatus().action}
        >
          <span
            className={`icon ${this.approvalStatus().icon}`}
            aria-hidden="false"
            aria-label={this.approvalStatus().copy}
          />
          <h4 className="action-text">{this.approvalStatus().copy}</h4>
        </div>
      </Tooltip>
    );
  }

  renderBulkCollectionRemoval(disabledClass) {
    return (
      <Tooltip
        tooltipId="BulkCollectionRemoval-tooltip"
        className="Remove-tooltip-component"
        tooltipContent={t('Remove')}
        type="light"
        offset={`{'top': -20}`}
      >
        <div
          className={`bulk-bar-item hover-item with-text ${disabledClass}`}
          onClick={() => this.removeAssetsFromCollection()}
        >
          <span className="icon bff-trash" aria-hidden="true" aria-label="" />
          <h4 className="action-text">{t('Remove')}</h4>
        </div>
      </Tooltip>
    );
  }

  render() {
    const { disableAction } = this.state;
    const { actionType } = this.props;
    const disabledClass = disableAction ? 'disabled-section' : '';

    if (actionType === 'BulkApproval') {
      return this.renderBulkApproval(disabledClass);
    }

    if (actionType === 'BulkCollectionRemoval') {
      return this.renderBulkCollectionRemoval(disabledClass);
    }

    return null;
  }
}

BulkAction.propTypes = {
  actionType: PropTypes.string.isRequired,
  activeCdnTracers: PropTypes.number,
  addRemoveSelected: PropTypes.func.isRequired,
  approver: PropTypes.bool.isRequired,
  assetsApproved: PropTypes.bool.isRequired,
  selectedAssetKeys: PropTypes.instanceOf(Set).isRequired,
};

BulkAction.defaultProps = {
  activeCdnTracers: null,
};
